import { Fragment, useState } from 'react';
import { COHORTS, QUARTER, type Cohort, type RmCohort } from '../shared/q3';
import type { Standing, Summary } from './standings';
import { COH, NearCard, StatusTag, nearEmptyNote, nearest } from './parts';
import { cr, pc, pc1 } from './format';
import { Link, to } from './router';
import ProductSplit from './ProductSplit';

type Filter = 'all' | 'q' | 'c' | 'r';

const FILTERS: Array<{ f: Filter; label: string; test: (r: Standing) => boolean }> = [
  { f: 'all', label: 'All', test: () => true },
  { f: 'q', label: 'Qualified', test: (r) => r.qualified },
  { f: 'c', label: 'Mandatory gap', test: (r) => r.status === 'Mandatory gap' },
  // Everyone not yet qualified, including those who've hit the total but are short on Mandatory.
  { f: 'r', label: 'In the race', test: (r) => !r.qualified },
];

interface Props {
  cohort: RmCohort;
  rows: Standing[];
  summary: Summary[];
  meId: string | null;
  /** Someone looked up on My Progress; their row is highlighted. */
  focusId: string | null;
  pace: number;
}

export function MeterRow({
  cls,
  label,
  value,
  done,
  pace,
  text,
}: {
  cls: string;
  label: string;
  value: number;
  done: boolean;
  pace?: number;
  /** Shown beside the bar instead of the bar's own %. */
  text?: string;
}) {
  return (
    <div className={'row ' + cls}>
      <span className="lab">{label}</span>
      <div className="tr">
        <div className={'fl' + (done ? ' done' : '')} style={{ width: Math.min(value, 1) * 100 + '%' }} />
        {pace !== undefined && <div className="pace" style={{ left: `calc(${(pace * 100).toFixed(1)}% - 1px)` }} />}
      </div>
      <span className="pv">{text ?? pc(value)}</span>
    </div>
  );
}

/** Back link, cohort tabs and the board's search box — shared by the RM and Star Force boards. */
export function ArenaNav({
  cohort,
  search,
  placeholder,
  onSearch,
}: {
  cohort: Cohort;
  search: string;
  placeholder: string;
  onSearch: (q: string) => void;
}) {
  return (
    <nav className="sk-nav" id="arena-nav" aria-label="Cohort">
      <Link className="sk-back" to={to('home')}>
        ← Overview
      </Link>
      <div className="sk-tabs" role="tablist">
        {COHORTS.map((c) => (
          <Link key={c} className={'sk-tab ' + COH[c].cls} to={to(c)} role="tab" aria-selected={c === cohort}>
            <span className={'th ' + COH[c].em} />
            {COH[c].name}
          </Link>
        ))}
      </div>
      <input
        className="sk-search"
        type="search"
        placeholder={placeholder}
        aria-label="Search this leaderboard"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
      />
    </nav>
  );
}

export default function ArenaView({ cohort, rows, summary, meId, focusId, pace }: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  /** Row whose product split is open (one at a time). */
  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = (id: string) => setOpenId((cur) => (cur === id ? null : id));
  const C = COH[cohort];
  const s = summary.find((x) => x.cohort === cohort)!;
  const mine = rows.filter((r) => r.cohort === cohort).sort((a, b) => a.rank - b.rank);
  const q = search.trim().toLowerCase();
  const test = FILTERS.find((x) => x.f === filter)!.test;
  const list = mine.filter((r) => test(r) && (!q || (r.rm + ' ' + r.head + ' ' + r.loc).toLowerCase().includes(q)));
  const near = nearest(mine);
  // Only people with business make the podium; ties share a medal number.
  const top = mine.filter((r) => r.ranked).slice(0, 3);
  const podium = [top[1], top[0], top[2]];

  return (
    <div className="sk-view sk-arena" id="arena">
      <ArenaNav
        cohort={cohort}
        search={search}
        placeholder="Search name, P&L head or city"
        onSearch={setSearch}
      />

      <div className="sk-banner2" id="arena-banner">
        <div className={'sk-emb ' + C.em} />
        <div className="sk-arena-h">
          <div className="sk-eyebrow">{QUARTER.label} leaderboard</div>
          <h2 className="sk-goldtext">{C.name}</h2>
          <div className="crit">{C.crit}</div>
          <div className="sk-kpis">
            <div className="sk-kpi">
              <div className="l">Qualified</div>
              <div className="v">
                <span>{s.qualified}</span>
                <span style={{ color: 'var(--sk-faint)', fontWeight: 400 }}> / </span>
                <span>{s.rms}</span>
              </div>
              <div className="s">of RMs in cohort</div>
            </div>
            <div className="sk-kpi">
              <div className="l">Achieved</div>
              <div className="v">{cr(s.totalCr)}</div>
              <div className="s">Core wealth + Mandatory</div>
            </div>
            <div className="sk-kpi">
              <div className="l">Mandatory</div>
              <div className="v">{cr(s.cat2Cr)}</div>
              <div className="s">Share {pc1(s.cat2Share)}</div>
            </div>
            <div className="sk-kpi">
              <div className="l">Cohort progress</div>
              <div className="v">{pc1(s.progress)}</div>
              <div className="s">Achieved vs total target</div>
            </div>
          </div>
        </div>
      </div>

      {/* No podium until someone has business on the board. */}
      {top.length > 0 && (
        <section className="sk-sec" id="podium">
          <div className="sk-sec-h">
            <div>
              <div className="sk-eyebrow">{C.name}</div>
              <h2>The podium</h2>
            </div>
            <p className="sk-note">Qualified RMs rank first, then by % of target</p>
          </div>
          <div className="sk-podium">
            {podium.map((r, i) => {
              if (!r) return null;
              const place = [2, 1, 3][i];
              return (
                <div key={r.empId} className={'sk-pod p' + place}>
                  {place === 1 && <div className="sk-crown">👑</div>}
                  <div className="sk-medal">{r.rank}</div>
                  <div className="nm">{r.rm}</div>
                  <div className="meta">{r.loc + ' · ' + r.head}</div>
                  <div className={'amt' + (place === 1 ? ' sk-goldtext' : '')}>{cr(r.totalCr)}</div>
                  <div className="pct">{pc(r.pct) + ' of ' + cr(r.targetCr)}</div>
                  <div className="tag">
                    <StatusTag r={r} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="sk-sec" id="leaderboard">
        <div className="sk-sec-h">
          <div>
            <div className="sk-eyebrow">{C.name}</div>
            <h2>Leaderboard</h2>
          </div>
          <div className="sk-tools">
            <div className="sk-seg" role="group" aria-label="Show">
              {FILTERS.map((x) => (
                <button key={x.f} aria-pressed={filter === x.f} onClick={() => setFilter(x.f)}>
                  {x.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="sk-board">
          <table className="sk-tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>RM</th>
                <th className="hide-m">P&amp;L head</th>
                <th className="hide-m hide-l">City</th>
                <th className="num hide-s hide-ml">Core</th>
                <th className="num hide-s">Mandatory</th>
                <th className="num">Total</th>
                <th>Progress</th>
                <th className="num hide-s hide-t">To qualify</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const isMe = r.empId === meId;
                const isOpen = r.empId === openId;
                const cls = [
                  'sk-xrow',
                  r.qualified && 'q',
                  r.ranked && r.rank <= 3 && 'r' + r.rank,
                  q && 'hl',
                  isMe && 'me',
                  r.empId === focusId && 'focus',
                  isOpen && 'open',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <Fragment key={r.empId}>
                    <tr
                      className={cls}
                      data-emp={r.empId}
                      tabIndex={0}
                      aria-expanded={isOpen}
                      title="Show business by product"
                      onClick={() => toggle(r.empId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle(r.empId);
                        }
                      }}
                    >
                      <td>
                        <span className="sk-rank">{r.ranked ? r.rank : '–'}</span>
                      </td>
                      <td className="sk-rm" title={r.rm}>
                        {r.rm}
                        {isMe && <span className="sk-you">YOU</span>}
                      </td>
                      <td className="hide-m sk-trunc" title={r.head}>
                        {r.head}
                      </td>
                      <td className="hide-m hide-l sk-trunc" title={r.loc}>
                        {r.loc}
                      </td>
                      <td className="num hide-s hide-ml">{cr(r.coreCr)}</td>
                      <td className="num hide-s">{cr(r.cat2Cr)}</td>
                      <td className="num">{cr(r.totalCr)}</td>
                      <td>
                        <div className="sk-meter">
                          <MeterRow cls="t" label="Total" value={r.pct} done={r.qualified} pace={pace} />
                          <MeterRow cls="c" label="Man." value={r.cat2Pct} done={false} />
                        </div>
                      </td>
                      <td className="num hide-s hide-t">{r.qualified ? '—' : cr(r.gapCr)}</td>
                      <td>
                        <StatusTag r={r} />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="sk-split-row">
                        <td colSpan={10}>
                          <ProductSplit p={r} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {!list.length && (
                <tr>
                  <td className="sk-empty" colSpan={10}>
                    No RMs match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="sk-note" style={{ marginTop: 10 }}>
          Tap a row to see that RM's business by product. Progress: the top bar is total business against target, the white tick is where today's pace says you should
          be; the lower bar is Mandatory (Category II, NCD, Corporate FD) against its minimum.
        </p>
      </section>

      <section className="sk-sec" id="striking-distance">
        <div className="sk-sec-h">
          <div>
            <div className="sk-eyebrow">{C.name}</div>
            <h2>Almost there</h2>
          </div>
          <p className="sk-note">Not yet qualified, closest to the line</p>
        </div>
        <div className="sk-strike">
          {near.map((r) => (
            <NearCard key={r.empId} r={r} showCohort={false} />
          ))}
          {!near.length && <p className="sk-note">{nearEmptyNote(mine, 'Everyone in this cohort has qualified!')}</p>}
        </div>
      </section>
    </div>
  );
}
